/**
 * React Query persister for IndexedDB storage.
 * Persists the query cache to IndexedDB for offline access.
 */
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client';
import type { QueryClient } from '@tanstack/react-query';
import { offlineStorage } from './OfflineStorageService';

const CACHE_KEY = 'panluma-react-query-cache';

/**
 * How long the app waits for the persisted cache before starting without it.
 *
 * `PersistQueryClientProvider` holds `isRestoring = true` until `restoreClient`
 * settles, and React Query fetches NOTHING while restoring. So an IndexedDB read
 * that never answers doesn't just cost the offline cache — it parks every query
 * in the app at `pending` / `fetchStatus: 'idle'` forever. That surfaces as
 * `isLoading === false` with no data and no error, i.e. every list renders its
 * "nothing here yet" empty state, indefinitely, with nothing in the console.
 *
 * IndexedDB stalls for reasons the app can neither see nor fix: an `open()` that
 * a still-connected older tab has blocked on a version upgrade (Safari restores
 * tabs across launches, so those connections outlive the browser session), and
 * WebKit transactions that occasionally never fire their events at all.
 *
 * The cache is an optimization; the live fetch is the product. Abandon a read
 * that hasn't answered and let the app come up.
 */
const RESTORE_TIMEOUT_MS = 3000;

/**
 * How long the app waits for the persisted cache to be DELETED before carrying
 * on without knowing whether it was.
 *
 * The twin of `RESTORE_TIMEOUT_MS`, added for the same reason and one bug
 * later. `removeClient` is awaited on every auth transition — `writeSession` on
 * every login, `logout()`, and the app lock's `endSession` — always ahead of the
 * `setState` or the navigation that ENDS the transition. An IndexedDB delete
 * that never answers is therefore not a lost cache-clear; it is a login that
 * never completes, on a spinner, with nothing in the console. The `openDB`
 * behind it can stay pending indefinitely for the reasons `OfflineStorageService`
 * gives in its own `blocked()` callback.
 *
 * Same hardware as the restore, so the same number.
 */
const REMOVE_TIMEOUT_MS = 3000;

/**
 * Set while a persisted-cache delete has been ATTEMPTED but not confirmed.
 *
 * The deadline above must not be paid for out of the security property
 * `removeClient` exists for: the persisted cache is shared by every user of a
 * browser profile, so a snapshot that survives a sign-out is the previous
 * user's lists waiting for the next reload to restore them (portal capstone
 * finding M3). Abandoning the delete is safe only because abandoning it is
 * RECORDED — synchronously, in localStorage, which answers when IndexedDB will
 * not — and `restoreClient` refuses to serve a snapshot while the record
 * stands. Storage the delete could not reach is foreign until a delete lands.
 */
const PURGE_PENDING_KEY = 'panluma_query_cache_purge_pending';

/** Reject with `reason` if `promise` hasn't settled within `ms`. */
async function withTimeout<T>(promise: Promise<T>, ms: number, reason: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(reason)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer!);
  }
}

/**
 * Query-key substrings whose queries are never persisted — real-time or
 * frequently changing data that would be misleading if restored stale.
 * Single source of truth for both `persistClient` and `shouldPersistQuery`.
 */
const NO_PERSIST_KEY_PATTERNS = ['notifications', 'websocket', 'typing', 'presence', 'health'];

/** Whether a query key matches one of the no-persist patterns. */
function matchesNoPersistPattern(queryKey: readonly unknown[]): boolean {
  const keyString = JSON.stringify(queryKey).toLowerCase();
  return NO_PERSIST_KEY_PATTERNS.some((pattern) => keyString.includes(pattern));
}

/**
 * Whether a value survives the structured-clone algorithm — the same algorithm
 * IndexedDB's `put` uses internally.
 *
 * A single query whose cached data holds a non-cloneable value (a function, DOM
 * node, etc. — classically a raw axios response carrying its `config.transformRequest`
 * function because a queryFn forgot to unwrap `.data`) makes the WHOLE `put`
 * throw `DataCloneError`. The persist then fails silently and the entire
 * persisted cache freezes at its last good snapshot — which is how a stale,
 * empty board list survived reloads on desktop web. Dropping just the offending
 * query keeps one poison pill from blocking persistence for every other query.
 *
 * Falls back to `true` (persist as-is, original behaviour) on engines without
 * `structuredClone` rather than dropping everything.
 */
function isStructuredCloneable(value: unknown): boolean {
  if (typeof structuredClone !== 'function') {
    return true;
  }
  try {
    structuredClone(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Delete the persisted snapshot, under a deadline, recording an attempt that
 * could not be confirmed.
 *
 * Never rejects: every caller is an auth transition that must complete whatever
 * the device storage does. The marker is raised BEFORE the delete, not after a
 * failure, so a document that is torn down mid-delete — a reload, an OTA bundle
 * commit — is also treated as unconfirmed rather than as done.
 */
async function deletePersistedCache(): Promise<void> {
  try {
    // Inside the try, like everything else here: this function is awaited on the
    // critical path of an auth transition and its contract is that it never
    // rejects, so it may not be the thing that throws.
    localStorage.setItem(PURGE_PENDING_KEY, '1');
    await withTimeout(
      offlineStorage.deleteQueryCache(CACHE_KEY),
      REMOVE_TIMEOUT_MS,
      'IndexedDB did not answer the query-cache delete',
    );
    localStorage.removeItem(PURGE_PENDING_KEY);
  } catch (error) {
    console.error('Failed to remove query cache:', error);
  }
}

/**
 * Custom persister that uses IndexedDB via OfflineStorageService.
 */
export const indexedDBPersister: Persister = {
  persistClient: async (client: PersistedClient): Promise<void> => {
    try {
      // Filter out queries that shouldn't be persisted
      const filteredClient: PersistedClient = {
        ...client,
        clientState: {
          ...client.clientState,
          queries: client.clientState.queries.filter((query) => {
            // Drop queries that have no cached data to restore. React Query
            // keeps prior `data` even when a refetch errors (offline cold-start
            // is exactly this case: success → offline → refetch fails →
            // status='error' but `data` still holds the cached payload). We
            // intentionally do NOT gate on `status === 'error'` here — doing
            // so wiped the IndexedDB cache every time the device went offline.
            if (query.state.data === undefined) {
              return false;
            }
            // Honour an explicit per-query opt-out: meta: { persist: false }.
            // Used for session-scoped data (e.g. announcement delivery) that
            // must be re-fetched fresh on every app load, never restored stale.
            if (query.meta?.persist === false) {
              return false;
            }
            // Check query key for patterns we don't want to persist
            if (matchesNoPersistPattern(query.queryKey)) {
              return false;
            }
            // Drop any query whose data can't survive structured clone so a
            // single non-cloneable query can't make the whole IndexedDB write
            // throw DataCloneError and freeze the entire persisted cache.
            if (!isStructuredCloneable(query)) {
              console.warn(
                '[offline] Skipping non-cloneable query during cache persist:',
                JSON.stringify(query.queryKey),
              );
              return false;
            }
            return true;
          }),
          mutations: [], // Don't persist pending mutations in Phase 1
        },
      };
      await offlineStorage.setQueryCache(CACHE_KEY, filteredClient);
    } catch (error) {
      console.error('Failed to persist query cache:', error);
    }
  },

  restoreClient: async (): Promise<PersistedClient | undefined> => {
    try {
      // A delete that was attempted and never confirmed leaves a snapshot that
      // may belong to somebody who has since signed out. Serve nothing, and take
      // the retry off the critical path — the app is starting, and this delete is
      // housekeeping that must not gate the first paint. Inside the try because a
      // storage read that throws must degrade to "no cache", never to a boot that
      // fails.
      if (localStorage.getItem(PURGE_PENDING_KEY) !== null) {
        void deletePersistedCache();
        return undefined;
      }
      const client = await withTimeout(
        offlineStorage.getQueryCache(CACHE_KEY),
        RESTORE_TIMEOUT_MS,
        'IndexedDB did not answer the query-cache read',
      ) as PersistedClient | null;
      return client || undefined;
    } catch (error) {
      console.error('Failed to restore query cache:', error);
      return undefined;
    }
  },

  removeClient: deletePersistedCache,
};

/**
 * Purge the entire query cache — both the in-memory `QueryClient` and the
 * persisted IndexedDB snapshot — so no data survives an auth transition.
 *
 * React Query's persisted cache is shared across every user on a browser
 * profile (one `PersistQueryClientProvider` at the app root). It is NOT cleared
 * on login/logout, so on a shared device a newly-logged-in user would briefly
 * render the previous user's cached lists before their own fetches replace them
 * (portal capstone finding M3 — customer B seeing customer A's shipments).
 *
 * Call this from every auth-transition path (login success AND logout).
 * `clear()` empties memory synchronously; `removeClient()` deletes the
 * IndexedDB copy so a subsequent reload can't restore the stale snapshot.
 */
export async function clearPersistedQueryCache(queryClient: QueryClient): Promise<void> {
  queryClient.clear();
  await indexedDBPersister.removeClient();
}

/**
 * Whether a query should be persisted, by query key alone.
 *
 * `persistClient` already applies this check (plus a `meta.persist: false`
 * opt-out) to every query. This helper is exported for callers that want to
 * reason about a key directly, e.g. in tests or prefetch logic.
 */
export function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  return !matchesNoPersistPattern(queryKey);
}

/**
 * Whether a query should be included in the dehydrated snapshot.
 *
 * React Query's default (`status === 'success'`) drops every query that has
 * ever errored — including queries that succeeded once online, were refetched
 * after the device went offline, errored on the refetch, but still hold the
 * previously-cached `data` in memory. That default plus our `staleTime: 0`
 * setting means going offline silently strips the cache of everything the user
 * viewed, defeating the whole point of read-only offline mode.
 *
 * Keep any query that holds defined `data` regardless of status — that's the
 * same predicate the IndexedDB persister filter below uses. The error itself
 * is still redacted by React Query's `shouldRedactErrors` default, so we
 * don't leak stale error objects on hydrate.
 */
function shouldDehydrateQueryForOffline(query: {
  state: { data: unknown };
}): boolean {
  return query.state.data !== undefined;
}

/**
 * Get persistence options for React Query.
 */
export function getPersistOptions() {
  return {
    persister: indexedDBPersister,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    buster: import.meta.env.VITE_VERSION || 'dev', // Invalidate on deployment
    dehydrateOptions: {
      shouldDehydrateQuery: shouldDehydrateQueryForOffline,
    },
  };
}
